/**
 * 🛡️ QRCODE MODAL MEJORADO - BASADO EN WHATICKET5
 * 
 * Este componente maneja el QR Code de forma segura
 * evitando errores que bloqueen la interfaz
 */

import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogContent, DialogTitle, IconButton, Paper, Typography, Box, Button, Divider, TextField } from "@material-ui/core";
import { FileCopy as CopyIcon, Close as CloseIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    padding: theme.spacing(1, 1, 2),
    maxWidth: 520,
  },
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingRight: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: 4,
  },
  root: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2, 0),
  },
  pairingCodeContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
  },
  pairingCodeText: {
    fontSize: "2rem",
    fontWeight: "bold",
    letterSpacing: "0.5rem",
    color: theme.palette.primary.main,
    fontFamily: "monospace",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  copyButton: {
    marginTop: theme.spacing(1),
  },
}));

const QrcodeModal = ({ open, onClose, whatsAppId, connectionType }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [sessionType, setSessionType] = useState(connectionType || "whatsapp");
  const [needPassword, setNeedPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [startError, setStartError] = useState("");
  const modalOpenedAtRef = useRef(0);
  const { user, socket } = useContext(AuthContext);

  const applySessionData = useCallback((session = {}) => {
    if (session.type) {
      setSessionType(session.type);
    }
    if (session.qrcode) {
      setQrCode(session.qrcode);
      setStartError("");
    }
    if (session.status === "OPENING" || session.status === "qrcode" || session.status === "PAIRING") {
      setStartError("");
    }
    if (
      session.status === "DISCONNECTED" &&
      (session.type === "telegram" || connectionType === "telegram") &&
      (session.lastDisconnectReason === "TELEGRAM_NO_API" ||
        session.lastDisconnectReason === "SESSION_START_FAILED")
    ) {
      const updatedAt = session.updatedAt ? new Date(session.updatedAt).getTime() : 0;
      if (updatedAt > 0 && updatedAt >= modalOpenedAtRef.current - 5000) {
        setStartError(
          session.lastDisconnectReason === "TELEGRAM_NO_API"
            ? "Falta configurar el API ID y API Hash de Telegram. Edita la conexión y pégalos (se crean en my.telegram.org), o contacta a soporte."
            : "No se pudo iniciar la conexión con Telegram. Cierra esta ventana e intenta de nuevo en unos segundos."
        );
      }
    }
    if (session.pairingCode) {
      setPairingCode(session.pairingCode);
    }
    if (session.lastDisconnectReason === "TELEGRAM_PASSWORD" || session.status === "PAIRING") {
      setNeedPassword(session.lastDisconnectReason === "TELEGRAM_PASSWORD");
    }
    if (session.status === "CONNECTED") {
      setNeedPassword(false);
    }
    if (session.lastDisconnectReason === "PAIRING_TIMEOUT") {
      const sessionUpdatedAt = session.updatedAt ? new Date(session.updatedAt).getTime() : 0;
      const isCurrentAttemptTimeout =
        sessionUpdatedAt > 0 && sessionUpdatedAt >= modalOpenedAtRef.current - 1000;

      if (!isCurrentAttemptTimeout) {
        return;
      }

      toast.info("El código QR expiró. Genera uno nuevo e intenta escanearlo inmediatamente.", {
        toastId: `qr-timeout-${whatsAppId}`
      });
      onClose();
    }
  }, [onClose, whatsAppId, connectionType]);

  useEffect(() => {
    // ✅ LIMPIAR QR CUANDO SE ABRE EL MODAL (para evitar mostrar QR viejo)
    if (open && whatsAppId) {
      modalOpenedAtRef.current = Date.now();
      setQrCode("");
      setPairingCode("");
      setPassword("");
      setNeedPassword(false);
      setStartError("");
      setSessionType(connectionType || "whatsapp");
    }
  }, [open, whatsAppId]);

  useEffect(() => {
    let active = true;
    let intervalId;

    const fetchSession = async () => {
      if (!whatsAppId || !open) return; // ✅ Solo fetch si el modal está abierto

      try {
        const { data } = await api.get(`/whatsappsession/${whatsAppId}`);

        if (active) {
          applySessionData(data);
        }
      } catch (err) {
        // ✅ Manejar errores de red/timeout de forma silenciosa
        // Los eventos de socket se encargarán de actualizar cuando el QR esté listo
        // Solo loguear errores críticos (no 404, 504, CORS que son temporales)
        if (err.response && err.response.status >= 500) {
          console.warn("Error obteniendo sesión WhatsApp (temporal):", err.message);
        }
        // No mostrar error al usuario - los eventos de socket traerán el QR cuando esté listo
      }
    };
    
    if (open && whatsAppId) {
      fetchSession();
      intervalId = setInterval(fetchSession, 2000);
    }

    return () => {
      active = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [whatsAppId, open, applySessionData]);

  useEffect(() => {
    if (!whatsAppId || !socket || !user.companyId) return;
    
    const companyId = user.companyId;

    // ✅ MANEJO SEGURO DE EVENTOS COMO WHATICKET5
    const onWhatsappData = (data) => {
      try {
        // ✅ CIERRE INMEDIATO SI SE RECIBE EVENTO DE CONEXIÓN EXITOSA
        if (data.action === "connected" && data.whatsapp && data.whatsapp.id === whatsAppId) {
          
          // ✅ ACTUALIZAR ESTADO LOCAL INMEDIATAMENTE
          if (window.updateWhatsAppStatus) {
            const updateEvent = new CustomEvent('updateWhatsAppStatus', {
              detail: {
                whatsAppId: whatsAppId,
                newStatus: {
                  status: "CONNECTED",
                  qrcode: "",
                  updatedAt: new Date().toISOString()
                }
              }
            });
            window.dispatchEvent(updateEvent);
          }
          
          // ✅ FORZAR ACTUALIZACIÓN DE LA LISTA DE CONEXIONES
          if (window.refreshConnections) {
            const refreshEvent = new CustomEvent('refreshConnections');
            window.dispatchEvent(refreshEvent);
          }
          
          // ✅ CERRAR MODAL INMEDIATAMENTE
          onClose();
          return;
        }
        
        // ✅ VERIFICACIÓN SEGURA: Verificar que data.session existe y tiene id
        if (data.action === "update" && data.session && data.session.id === whatsAppId) {
          // ✅ MANEJAR ERRORES INFORMATIVOS (QR_EXPIRED, MAX_QR_RETRIES_REACHED)
          // ⚠️ NOTA: Solo cerrar modal aquí, NO mostrar toast (ya se muestra en Connections para evitar duplicados)
          if (data.error) {
            if (data.error.code === "QR_EXPIRED" || data.error.code === "MAX_QR_RETRIES_REACHED") {
              // Cerrar modal inmediatamente (el toast ya se muestra en Connections)
              onClose();
              return;
            }
          }
          
          // ✅ ACTUALIZAR QR CODE Y PAIRING CODE SI ESTÁN DISPONIBLES
          applySessionData(data.session);
          
          // ✅ SOLO CERRAR SI ESTÁ REALMENTE CONECTADO (no si está en PAIRING o qrcode)
          const isWaitingForQR = data.session.status === "PAIRING" || 
                                data.session.status === "qrcode" || 
                                data.session.status === "OPENING";
          
          if (data.session.status === "CONNECTED" && !isWaitingForQR) {
            onClose();
            return;
          }
        }
        
        // ✅ MANEJO ALTERNATIVO: Si los datos vienen en data.whatsapp
        if (data.action === "update" && data.whatsapp && data.whatsapp.id === whatsAppId) {
          // ✅ ACTUALIZAR QR CODE Y PAIRING CODE SI ESTÁN DISPONIBLES
          applySessionData(data.whatsapp);
          
          // ✅ SOLO CERRAR SI ESTÁ REALMENTE CONECTADO (no si está en PAIRING o qrcode)
          // NO cerrar si está en proceso de generar QR (PAIRING, qrcode, OPENING)
          const isWaitingForQR = data.whatsapp.status === "PAIRING" || 
                                 data.whatsapp.status === "qrcode" || 
                                 data.whatsapp.status === "OPENING";
          
          if (data.whatsapp.status === "CONNECTED" && !isWaitingForQR) {
            // ✅ ACTUALIZACIÓN INMEDIATA DE LA INTERFAZ
            const updateEvent = new CustomEvent('updateWhatsAppStatus', {
              detail: {
                whatsAppId: whatsAppId,
                newStatus: {
                  status: "CONNECTED",
                  qrcode: "",
                  updatedAt: new Date().toISOString()
                }
              }
            });
            window.dispatchEvent(updateEvent);
            
            const refreshEvent = new CustomEvent('refreshConnections');
            window.dispatchEvent(refreshEvent);
            
            onClose();
            return;
          }
          
          // ✅ SI ESTÁ EN PAIRING O QRCODE, ESPERAR A QUE LLEGUE EL QR
          // NO cerrar, solo esperar
        }
      } catch (error) {
        // ✅ NO PROPAGAR EL ERROR PARA EVITAR BLOQUEO
      }
    };

    // ✅ VERIFICAR QUE SOCKET TIENE EL MÉTODO 'on' ANTES DE USARLO
    if (socket && typeof socket.on === 'function') {
      // ✅ REGISTRAR LISTENER DE FORMA SEGURA PARA AMBOS EVENTOS
      socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);
      socket.on(`company-${companyId}-whatsapp`, onWhatsappData);

      return () => {
        // ✅ CLEANUP AUTOMÁTICO CON VERIFICACIÓN
        if (socket && typeof socket.off === 'function') {
          socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
          socket.off(`company-${companyId}-whatsapp`, onWhatsappData);
        }
      };
    }
  }, [whatsAppId, onClose, user.companyId, socket, open, applySessionData]);

  const handleCopyPairingCode = () => {
    if (pairingCode) {
      navigator.clipboard.writeText(pairingCode);
      toast.success("Código de emparejamiento copiado al portapapeles");
    }
  };

  const isTelegram = sessionType === "telegram" || connectionType === "telegram";

  const handleSubmitTelegramPassword = async () => {
    if (!password.trim() || !whatsAppId) return;
    setSavingPassword(true);
    try {
      await api.post(`/whatsappsession/${whatsAppId}/telegram-password`, { password: password.trim() });
      toast.success("Contraseña enviada. Espera la conexión.");
      setPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.error || "No se pudo enviar la contraseña");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.titleRow}>
        <Box>
          <Typography className={classes.title}>
            {isTelegram ? "Conectar con Telegram" : "Conectar con WhatsApp"}
          </Typography>
          <Typography className={classes.subtitle}>
            {isTelegram
              ? "Abre Telegram en el celular → Ajustes → Dispositivos → Vincular dispositivo de escritorio y escanea el código."
              : "Abre WhatsApp en tu celular, ve a Configuración → Dispositivos vinculados y escanea el código."}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Paper elevation={0}>
          <div className={classes.root}>
            {qrCode ? (
              <QRCodeSVG value={qrCode} size={260} style={{ backgroundColor: "white", padding: 12, borderRadius: 12 }} />
            ) : startError ? (
              <span style={{ color: "#c62828", textAlign: "center", padding: "0 12px" }}>{startError}</span>
            ) : (
              <span>{i18n.t("qrCode.waiting")}</span>
            )}
          </div>

          {isTelegram && needPassword ? (
            <Box className={classes.pairingCodeContainer}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Esta cuenta tiene verificación en dos pasos. Escribe la contraseña de Telegram.
              </Typography>
              <TextField
                fullWidth
                type="password"
                variant="outlined"
                margin="dense"
                label="Contraseña 2FA"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                variant="contained"
                color="primary"
                disabled={savingPassword || !password.trim()}
                onClick={handleSubmitTelegramPassword}
                className={classes.copyButton}
              >
                Enviar contraseña
              </Button>
            </Box>
          ) : null}
          
          {!isTelegram && pairingCode ? (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <Box className={classes.pairingCodeContainer}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  ¿Prefieres usar el código de 8 dígitos?
                </Typography>
                <Typography className={classes.pairingCodeText}>
                  {pairingCode}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyPairingCode}
                  className={classes.copyButton}
                >
                  Copiar código
                </Button>
              </Box>
            </>
          ) : !isTelegram ? (
            <Typography align="center" variant="body2" color="textSecondary">
              ¿Prefieres usar el código de 8 dígitos? Se mostrará aquí cuando esté disponible.
            </Typography>
          ) : null}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
